/* Opening someone's writing, in the right order.
 *
 * The order matters more than it looks. A device must not plant the starter
 * content until the server has told it whether this account already holds any —
 * otherwise the second device you sign in on seeds locally with today's
 * timestamps, and its pristine "Chapter One" outranks the one you actually
 * wrote last week.
 */

import { openStore, readDatabase } from './store.js';
import { createSync } from './sync.js';
import { createTransport } from './identity.js';

const LEGACY_DB = 'longhand';

/**
 * Each account gets its own local database; two people, one browser, no bleed.
 *
 * It throws rather than improvising a name. A missing id is a bug upstream, and
 * the quiet version of this — `longhand-undefined` — is worse than a crash: the
 * app opens a second, empty database, sync dutifully fills it from the server
 * so the writing still looks right, and everything that *doesn't* sync (the
 * companion's conversations, the sync cursor) is silently left behind in the
 * database nobody is reading any more.
 */
export const databaseFor = user => {
  if (!user?.id) throw new Error('Cannot open a database: no account id.');
  return 'longhand-' + user.id;
};

export async function openForUser(user, { onStatus = () => {} } = {}) {
  const store = await openStore({ name: databaseFor(user), autoSeed: false });
  const transport = createTransport();

  let adopted = 0;
  let seeded = false;

  /**
   * Decide whether this device should plant the starter content. Safe to call
   * repeatedly: it does nothing until a sync has actually succeeded, and
   * nothing at all once anything exists.
   */
  const seedIfFirst = () => {
    if (seeded || !store.isEmpty() || transport.seeded !== false) return false;
    seeded = store.plantSeed();
    return seeded;
  };

  // A first login that cannot reach the server leaves an empty desk, because
  // seeding without the server's answer is how you end up with duplicates.
  // Watch for the sync recovering so it fills in without needing a reload.
  const sync = createSync(store, transport, {
    onStatus: (status) => {
      if (status.phase === 'idle' && status.lastSyncedAt && seedIfFirst()) {
        sync.sync().catch(() => {});
      }
      onStatus(status);
    }
  });

  // Writing from before there were accounts lives in the un-namespaced
  // database. Take it rather than letting it vanish behind a login.
  if (store.isEmpty()) {
    const legacy = await readDatabase(LEGACY_DB);
    if (legacy) adopted = store.adopt(legacy);
  }

  try {
    await sync.sync();

    // Only now is it safe to decide. An empty store plus an untouched account
    // means this really is the first device.
    if (seedIfFirst()) await sync.sync();
  } catch (err) {
    // Offline, or the service is down. Neither is a reason to withhold someone's
    // writing — the loop will catch up when it can. The one thing we must not do
    // is seed, because we still do not know whether the account has content.
    console.warn('Longhand: first sync failed —', err.message);
  }

  sync.start();
  return { store, sync, adopted, seeded };
}
